import Big from 'big.js';
import * as bootstrap from 'bootstrap';

export interface ShippingOverrideHandle {
    // Current override, or undefined when the admin hasn't set one.
    // Summary reads this on every cart fetch so the override survives
    // address/promo/exempt-driven recomputes.
    get(): InstanceType<typeof Big> | undefined;
}

export interface ShippingOverrideDeps {
    // Fires whenever the override is applied, edited, or cleared. The page
    // forwards this into summary.setShippingOverride so the next /carts/:id
    // fetch carries the override and tax recomputes against it.
    onOverrideChange: (override: InstanceType<typeof Big> | undefined) => void;
}

export function SetupShippingOverride(deps: ShippingOverrideDeps): ShippingOverrideHandle {
    const $toggle = document.getElementById('shipping-override-toggle') as HTMLButtonElement;
    const $input = document.getElementById('shipping-override-input') as HTMLInputElement;
    const $apply = document.getElementById('shipping-override-apply') as HTMLButtonElement;
    const $clear = document.getElementById('shipping-override-clear') as HTMLButtonElement;
    const $badge = document.getElementById('shipping-override-badge') as HTMLElement;
    const $shipping = document.getElementById('summary-shipping') as HTMLElement;

    const dropdown = bootstrap.Dropdown.getOrCreateInstance($toggle);

    // undefined = no override. Big(0) is a legitimate override ("force free"),
    // distinct from undefined.
    let override: InstanceType<typeof Big> | undefined;

    function parseInput(): InstanceType<typeof Big> | undefined {
        const raw = $input.value.trim();
        if (raw === '') {
            return undefined;
        }
        const v = parseFloat(raw);
        if (isNaN(v) || v < 0) {
            return undefined;
        }
        return Big(v);
    }

    function renderActiveState() {
        if (override === undefined) {
            $badge.classList.add('d-none');
            $clear.classList.add('d-none');
            // Drop any prior highlight on the shipping value. Bootstrap's
            // text-warning is the only state class we add — guard so we
            // don't strip classes we never added.
            $shipping.classList.remove('text-warning');
            // text-muted on the toggle is the resting tone; keep it.
            $toggle.classList.remove('text-warning');
            $toggle.classList.add('text-muted');
            return;
        }
        $badge.classList.remove('d-none');
        $clear.classList.remove('d-none');
        $shipping.classList.add('text-warning');
        $toggle.classList.remove('text-muted');
        $toggle.classList.add('text-warning');
    }

    function commit(next: InstanceType<typeof Big> | undefined) {
        const same =
            (override === undefined && next === undefined) ||
            (override !== undefined && next !== undefined && override.eq(next));
        if (same) {
            return;
        }
        override = next;
        renderActiveState();
        deps.onOverrideChange(override);
    }

    $apply.addEventListener('click', () => {
        const next = parseInput();
        if (next === undefined) {
            // Treat empty/invalid Apply as a no-op rather than clearing —
            // a stray click on Apply with an empty input shouldn't drop
            // an existing override. Keep the dropdown open so the admin can
            // either fix the value or click Cancel/outside to dismiss.
            $input.focus();
            return;
        }
        commit(next);
        // Apply/Clear are terminal actions — the admin is done with the
        // menu. data-bs-auto-close="outside" deliberately keeps the dropdown
        // open while typing, but doesn't dismiss it on button clicks
        // inside, so we hide() programmatically here.
        dropdown.hide();
    });

    $clear.addEventListener('click', () => {
        $input.value = '';
        commit(undefined);
        dropdown.hide();
    });

    // Enter submits Apply; Escape clears the input and dismisses focus.
    $input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
            e.preventDefault();
            $apply.click();
        }
    });

    // Sync the input to the current override and focus it whenever the
    // dropdown opens. Selecting the content lets the admin type to replace
    // an existing value without having to clear first. Bootstrap fires
    // shown.bs.dropdown on the toggle after the menu is fully positioned,
    // so the focus call isn't fighting the open animation.
    $toggle.addEventListener('shown.bs.dropdown', () => {
        $input.value = override === undefined ? '' : override.toFixed(2);
        $input.focus();
        $input.select();
    });

    renderActiveState();

    return {
        get: () => override,
    };
}
